import {
	coarsenIsoDate,
	generateYearRange,
	type DatePrecision,
	todayIsoDate,
	todayIsoMonth,
} from "./promise-metadata";

export interface DatePrecisionFieldProps {
	value: string;
	precision: DatePrecision;
	onChange: (dayIso: string, precision: DatePrecision) => void;
}

export function DatePrecisionField({
	value,
	precision,
	onChange,
}: DatePrecisionFieldProps) {
	const maxDay = todayIsoDate();
	const maxMonth = todayIsoMonth();
	const years = generateYearRange();

	const handlePrecisionChange = (next: DatePrecision) => {
		if (next === precision) return;
		const base = value || maxDay;
		const coarsened = coarsenIsoDate(base, next);
		onChange(coarsened, next);
	};

	const handleDayChange = (dayIso: string) => {
		onChange(dayIso, precision);
	};

	const handleMonthChange = (monthValue: string) => {
		if (!monthValue) {
			onChange("", precision);
			return;
		}
		onChange(`${monthValue}-01`, precision);
	};

	const handleYearChange = (yearStr: string) => {
		if (!yearStr) {
			onChange("", precision);
			return;
		}
		onChange(`${yearStr}-01-01`, precision);
	};

	const monthInputValue = value.length >= 7 ? value.slice(0, 7) : "";
	const yearInputValue =
		value.length >= 4 ? value.slice(0, 4) : "";

	return (
		<div className="date-precision-field">
			{precision === "day" && (
				<input
					type="date"
					className="date-input"
					value={value}
					max={maxDay}
					onChange={(e) => handleDayChange(e.target.value)}
				/>
			)}
			{precision === "month" && (
				<input
					type="month"
					className="date-input"
					value={monthInputValue}
					max={maxMonth}
					onChange={(e) => handleMonthChange(e.target.value)}
				/>
			)}
			{precision === "year" && (
				<select
					className="date-input date-select"
					value={yearInputValue}
					onChange={(e) => handleYearChange(e.target.value)}
				>
					<option value="">Select year</option>
					{years.map((y) => (
						<option key={y} value={String(y)}>
							{y}
						</option>
					))}
				</select>
			)}
			<div className="segmented-control date-precision-segments">
				{(["day", "month", "year"] as const).map((p) => (
					<button
						key={p}
						type="button"
						className={precision === p ? "active" : ""}
						onClick={() => handlePrecisionChange(p)}
					>
						{p.charAt(0).toUpperCase() + p.slice(1)}
					</button>
				))}
			</div>
		</div>
	);
}
